import { useViewerControl } from 'libs/rendering/gltf/hooks';
import { useContextManager } from 'apps/context/hooks';
import { analytics } from 'core/analytics';
import { useClientStore, useRouterStore } from 'apps/hooks';
import { UiModes } from 'libs/rendering/gltf/config';
import { CADPanelType, CADPanelID } from 'constants/cad';
import { isMobile } from 'styles/utils';
import { useGetElementInfo } from 'hooks/useGetElementInfo';
import {
  CADComparisonHeight,
  CADGap,
  CADSidebarWidth,
  ConflictAlertHeight,
} from 'styles/constants';
import { isInitiativeSlug } from 'utils';

export type CADManagerProps = {
  mobileMode?: boolean;
  isPreview?: boolean;
};

// - keeping this here as reference, this piece of code reacts to selecting
// a markup item, so selecting a Measurement will trigger this code
// - this could be used to implement moving the camera to look at a Measurement
// when the user clicks it, same as we do for Notes
// export const useEffectLastSelMkp = () => {
//   const viewerControl = useViewerControl();
//   const { viewer } = viewerControl;
//   const { selectionManager } = viewer || {};

//   // update selection mode if measurement selected
//   const lastSelMkp = selectionManager.GetLastMarkupSelection();
//   useEffect(() => {
//     if (lastSelMkp) {
//       viewerControl.StartMeasureMode();
//     }
//   }, [lastSelMkp]);

//   return {
//     lastSelMkp,
//   };
// };

export const useCADManager = () => {
  const viewerControl = useViewerControl();
  const contextManager = useContextManager();
  const clientStore = useClientStore();
  const { route } = useRouterStore();

  const { width: panelWidth = 0 } = useGetElementInfo(`#${CADPanelID}`, [
    viewerControl.activePanelType,
    viewerControl.uiMode,
  ]);

  const {
    currentSpaceData,
    flags: profileFlag,
    slugFromRouteOrSpace,
  } = clientStore;

  const shouldShowDiffOrMeasurement = isInitiativeSlug(slugFromRouteOrSpace)
    ? JSON.parse(currentSpaceData?.flags || '{}').cadDiff
    : (profileFlag as { cadDiff: boolean })?.cadDiff;

  const { viewerVisible, viewer, cadFile, debugFeatures } = viewerControl;
  const { notePad, settings, isDiffing } = viewer || {};

  const currCamera = settings?.CurrentCamera();

  const { pmi, assembly } = cadFile || {};
  const rootId = assembly?.GetRoot()?.uuid;
  const itemId = currCamera?.GetItem()?.uuid;
  const allMeasurements = pmi?.GetMeasurements();

  // - we care about itemMeasurements, aka the measurements for the thing we're
  // currently looking at, most things using useCADManager will want to know
  // about those, even if it is only to determine if there is more then one
  // to display a button or not
  const itemMeasurements = new Map<string, any[]>();
  // - the order of the map is somewhat relevant, to avoid having to do some
  // nasty sorting in MeasurementsPanel using both the measurements and annotations
  // from contextManager together, I decided it is easier to just have the Map
  // be created in always the same order
  // - so the rootId will always come first, so that measurements on the root
  // appear before measurements on sub-assemblies, the rest seems to work fine
  // by itself when just sorting using the selfIx only
  itemMeasurements.set(rootId, []);
  for (const key of allMeasurements?.keys() || []) {
    // - I don't understand why I have to use notePad.GetMeasurement, and can't
    // use allMeasurements.get instead
    // - if I don't use it then the changes to the started measurement in
    // useMeasurementMenu are not registered, I suspect that it has something
    // to do with how we wrap the wasmObservables, but I can't really tell
    // what the problem is yet
    const m = notePad.GetMeasurement(key);
    if (m && !m.isDeleted && !m.isTemp) {
      if (!itemMeasurements.has(m.forCadId)) {
        itemMeasurements.set(m.forCadId, []);
      }
      itemMeasurements.get(m.forCadId).push(m);
    }
  }
  // - add this so the code consuming the itemMeasurements assumes that
  // itemMeasurements can safely assume that itemMeasurements.get(itemId)
  // returns an empty list
  if (!itemMeasurements.has(itemId)) {
    itemMeasurements.set(itemId, []);
  }

  let sortedMeasurements: any[] = [];
  for (const measures of itemMeasurements.values()) {
    sortedMeasurements.push(...measures);
  }
  sortedMeasurements.sort((a: any, b: any) => {
    if (a.selfIx < b.selfIx) {
      return -1;
    } else if (a.selfIx > b.selfIx) {
      return 1;
    } else {
      // - we're sorting allMeasurements here, so they will have duplicate selfIx'es
      // - we should have some other thing to sort by, like the annotation timestamp,
      // getting that here is tricky, we either add it to the Measurement in the viewer,
      // which I don't like because it duplicates the timestamp and then we have it
      // in two places, or we use the annotations list from contextManager here but
      // means some complex code to fit both the allMeasurements and the annotations
      // together
      // - TODO: for now the useCADManager inserts allMeasurements in a deterministic
      // way so that that all measurements should always appear in the same order, but
      // that should be replaced with a proper sorting mechanism here
      return 0;
    }
  });

  const isConflict = !!route.params?.repoId && route.params?.conflict;
  const { selectionManager, module: viewerModule } = viewer || {};
  const selMode = selectionManager?.GetSelectionMode();
  const selModeEnum = viewerModule?.SelectionMode;
  const hasIntermediatorRole = clientStore.hasRole('INTERMEDIATOR');

  const isIntermediatorDebug = hasIntermediatorRole && debugFeatures;

  const isTwoSided =
    (viewer?.isDiffLoading || viewer?.isDiffing) &&
    !viewer.diffTool?.isSuperposed;
  const isDiffPage = isDiffing || isTwoSided;

  const getCADTotalHeaderHeight = () => {
    const diffPageHeadHeight =
      CADComparisonHeight + clientStore.PageHeaderHeight;

    if (isDiffPage && isConflict) {
      return diffPageHeadHeight + ConflictAlertHeight;
    }
    if (isDiffPage) {
      return diffPageHeadHeight;
    }

    return clientStore.PageHeaderHeight;
  };

  const totalHeaderHeight = getCADTotalHeaderHeight();

  const panelAndSidebarWidth =
    isDiffPage || !viewer ? CADGap : panelWidth + CADSidebarWidth + CADGap;

  const showAnnotationsEyes = viewer?.notePad?.NoteCount() > 0;

  const instance = currCamera?.GetInstance();
  const body = instance?.GetBody();
  const brep = body?.GetBrep();

  const restrictedCamera = !currCamera || currCamera?.Is2D?.() || currCamera?.IsPlaneXY?.();
  const shouldShowMeasureBtn =
    !isDiffing &&
    currCamera?.Is3D() &&
    !currCamera.IsPlaneXY() &&
    !restrictedCamera &&
    viewerVisible;

  const hasSeparation = currCamera?.IsInstance()
    ? currCamera?.GetInstance()?.GetBody()?.HasSeparation()
    : pmi?.MeshSeparationCount() > 0;

  const showAnnotatePart =
    !restrictedCamera &&
    currCamera?.Is3D() &&
    (!currCamera?.IsInstance() || !hasSeparation);

  const showAnnotateGeometry = restrictedCamera || hasSeparation;

  const shouldShowAnnotationWays = showAnnotatePart || showAnnotateGeometry;

  const isActiveGeometry =
    selModeEnum && selMode === selModeEnum.SelectionModeLink;

  const isActivePart =
    selModeEnum && selMode === selModeEnum.SelectionModeInstanceLink;

  const shouldShowAnnotationPin = isActiveGeometry || isActivePart;

  const shouldShowDfmBtn =
    !isDiffing &&
    currCamera?.Is3D() &&
    !currCamera.IsPlaneXY() &&
    currCamera?.IsInstance() &&
    hasIntermediatorRole;

  const shouldShowAssemblyTree =
    viewerVisible &&
    currCamera &&
    !isDiffing &&
    viewer &&
    (!currCamera?.IsInstance() || isIntermediatorDebug || brep);

  const lastSelMkp = selectionManager?.GetLastMarkupSelection?.();

  const isActiveAnnotation =
    viewerControl.activePanelType === CADPanelType.AnnotationPanel ||
    shouldShowAnnotationPin;

  const isOpenMeasurementFloatWindow =
    (selModeEnum && selMode === selModeEnum.SelectionModeMeasurements) ||
    lastSelMkp;

  const startMeasurementAddition = () => {
    if (selMode === selModeEnum.SelectionModeMeasurements) {
      // stop adding measurement
      viewerControl.Escape();
      // hide toolbar options
      viewerControl.toolBarExpanded = null;
    } else {
      // start adding measurement
      viewerControl.StartMeasureMode();
    }
  };

  // Measurements
  const toggleExpandMeasurements = () => {
    if (viewerControl.toolBarExpanded !== 'measurement') {
      // show measurements menu
      viewerControl.toolBarExpanded = 'measurement';
    } else {
      viewerControl.Escape();
      viewerControl.toolBarExpanded = null;
    }
    analytics.sendEvent('viewer-measurements-toggle');
  };

  const handleAssemblyTreeBtn = () => {
    viewerControl.UpdateUiMode(UiModes.assembly);
  };

  const closePanel = () => {
    viewerControl.UpdateUiMode(UiModes.collapsed);
  };

  const closeMeasurementFloatWindow = () => {
    // delete temp measurement
    pmi.AbortStartedMeasurement();
    // delete measurmenet selection
    if (lastSelMkp) {
      selectionManager.ClearSelection();
    }
    // exit measurement add mode
    viewerControl.SyncSelectionMode();
  };

  const showAnnotationsOption = () => {
    if (contextManager) {
      const currPmi = viewerControl.cadFile?.pmi;
      const hasCameraInstance = currCamera?.IsInstance()
        ? currCamera?.GetInstance()?.GetBody()?.HasSeparation()
        : currPmi?.MeshSeparationCount() > 0;

      const showAnnotatePartPin =
        (currCamera?.Is3D() && !currCamera.IsInstance()) ||
        (currCamera?.Is3D() && currCamera.IsInstance() && !hasCameraInstance);

      const showAnnotateFeature = currCamera?.Is2D() || hasCameraInstance;
      const showHideAnnotations = notePad?.NoteCount() > 0;

      return (
        (showAnnotatePartPin || showAnnotateFeature || showHideAnnotations) &&
        viewerVisible
      );
    }

    return false;
  };

  const toggleAnnotations = () => {
    if (isMobile) viewerControl.SetLightModalVisibility(false);
    if (contextManager?.annotationsAvailable) {
      // show/hide pin addition menu
      if (viewerControl.toolBarExpanded !== 'pin') {
        viewerControl.toolBarExpanded = 'pin';
      } else {
        viewerControl.toolBarExpanded = null;
        if (selectionManager?.IsSelectionModeLink()) {
          viewerControl.DisableAnnotationMode();
        }
      }
      analytics.sendEvent('viewer-annotations-toggle');
    }
  };

  const handleClickAnnotationEye = () => {
    viewerControl.viewer.notePad.visible =
      !viewerControl.viewer.notePad.visible;
    const eventAction = viewerControl.viewer.notePad.visible ? 'hide' : 'show';
    analytics.sendEvent(`viewer-annotations-${eventAction}`);
  };

  const toggleAnnotationPanel = () => {
    viewerControl.SetActivePanel(CADPanelType.AnnotationPanel);
  };

  const toggleMeasurePanel = () => {
    viewerControl.SetActivePanel(CADPanelType.MeasurementPanel);
  };

  const startPinAddition = (isGeometry: boolean) => {
    const selectionEnum = viewerControl.viewer.module.SelectionMode;
    closePanel();

    const isModeLink = selMode === selModeEnum.SelectionModeLink;
    const isModeInstanceLink =
      selMode === selModeEnum.SelectionModeInstanceLink;

    const shouldSyncSelectionMode =
      (isGeometry && isModeLink) || (!isGeometry && isModeInstanceLink);

    if (shouldSyncSelectionMode) {
      // deactivate: geometric feature (face, edge) selection mode / instance (part) selection mode
      viewerControl.SyncSelectionMode();
    }

    // init pin selection...
    if (isGeometry && !isModeLink) {
      // activate
      viewerControl.viewer.selectionManager.SetSelectionMode(
        selectionEnum.SelectionModeLink,
      );
    } else if (!isGeometry && !isModeInstanceLink) {
      // activate
      viewerControl.viewer.selectionManager.SetSelectionMode(
        selectionEnum.SelectionModeInstanceLink,
      );
    }

    if (viewerControl.viewer?.notePad) {
      // force all pins visible
      viewerControl.viewer.notePad.visible = true;
    }
  };

  const handleClickAnnotationPart = () => {
    startPinAddition(false);
    analytics.sendEvent('viewer-annotate-part');
  };

  const handleClickAnnotationGeometry = () => {
    startPinAddition(true);
    analytics.sendEvent('viewer-annotate-feature');
  };

  // TODO: this is way too much stuff, try to split it and
  // remove as much as possible from here
  return {
    selMode,
    isDiffing,
    isTwoSided,
    lastSelMkp,
    closePanel,
    viewerControl,
    shouldShowDfmBtn,
    showAnnotationsEyes,
    showAnnotationsOption,
    shouldShowMeasureBtn,
    shouldShowAssemblyTree,
    handleAssemblyTreeBtn,
    startMeasurementAddition,
    closeMeasurementFloatWindow,
    shouldShowDiffOrMeasurement,
    isOpenMeasurementFloatWindow,
    handleClickAnnotationPart,
    handleClickAnnotationGeometry,
    toggleAnnotations,
    toggleMeasurePanel,
    toggleAnnotationPanel,
    handleClickAnnotationEye,
    showAnnotatePart,
    isActivePart,
    toggleExpandMeasurements,
    isActiveGeometry,
    shouldShowAnnotationPin,
    shouldShowAnnotationWays,
    showAnnotateGeometry,
    panelAndSidebarWidth,
    totalHeaderHeight,
    isActiveAnnotation,
    stopDiffing: viewer?.stopDiffing,
    startDiffing: viewer?.startDiffing,
    itemMeasurements,
    sortedMeasurements,
    itemId,
  };
};
